import React, { useEffect, useState } from 'react';
import { Button, Divider, Form, Row, Tooltip } from 'antd';

import { MapRouteLink } from 'leash/components/mapRouteLink/MapRouteLink';
import { SelectTravelMethod } from 'leash/components/fields/selectTravelMethod/SelectTravelMethod';
import { FieldGroundTransporter } from 'leash/components/fields/fieldGroundTransporter/FieldGroundTransporter';
import { Notes } from 'leash/components/notes/Notes';
import { useDetailsState } from 'leash/pages/details/useDetailsState';
import { DetailsHeader } from 'leash/pages/details/detailsHeader/DetailsHeader';
import {
  handleOnSubmit,
  populateFormOnLoad,
} from 'leash/pages/details/proposalForm/proposalForm.utilities';
import {
  isCanceled,
  isFinalized,
  isRejected,
} from 'leash/pages/details/utilities/details.utilities';
import { buildEligibilityTravelMethods } from 'leash/utilities/travelEligibility/travelEligibility';
import { TravelMethodId } from 'leash/utilities/const';
import { LeashContent } from 'leash/utilities/localStorage/leashContentStorage';
import { FieldDate } from 'shared/components/fields/fieldDate/FieldDate';
import { notify } from 'utilities/notifications/notify';
import { apiPackage } from 'utilities/http/api/leash/apiPackage';
import { log } from 'utilities/log/log';
import { testId } from 'leash/utilities/test/dataTestId';
import { TravelPlan, TransporterCompany } from 'types';

import { ProposalButtons, ProposalFormStyled, ProposalStyled } from './Proposal.styles';

interface Props {
  disabledFinalizeBySequences: boolean;
}

export function ProposalForm({ disabledFinalizeBySequences }: Props) {
  const { state, dispatch } = useDetailsState();
  const { travelEligibility, travelEligibilitySelected } = state;
  const travelPlan = state?.travelPlan as TravelPlan;
  const { travelPackage } = travelPlan;
  const { travelProposal, travelMethodId } = travelPackage;
  const workflowStatus = travelPackage?.workflowStatus;
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form] = Form.useForm();

  const isDisabledFinalizeButton =
    isFinalized(workflowStatus) ||
    isCanceled(workflowStatus) ||
    disabledFinalizeBySequences;

  const handleSaveFormData = async () => {
    setIsSaving(true);
    if (isRejected(travelPlan.travelPackage.workflowStatus)) {
      const respReject = await apiPackage.revertRejectionTravelPlan(travelPackage.id);
      if (respReject.isOk) {
        const workflowStatus = respReject.json.travel.workflowEntity.currentStatus.name;
        dispatch({ type: 'SET_WORK_FLOW_STATUS', workflowStatus });
      }
    }
    const values = form.getFieldsValue();

    await handleOnSubmit(values, travelPlan, dispatch);
    setIsSaving(false);
  };

  const handleFormOnFinish = async () => {
    setIsSubmitting(true);
    try {
      const response = await apiPackage.finalize(travelPlan.travelPackage.id);
      if (!response.isOk) {
        notify.error("Couldn't finalize the proposal");
        return;
      }

      const workflowStatus = response.json.travel.workflowEntity.currentStatus.name;
      dispatch({ type: 'SET_WORK_FLOW_STATUS', workflowStatus });

      notify.success('Proposal finalized!', 'proposal-finalized-notification');
    } catch (finalizeError) {
      notify.error('There was an error, please try again later');
      log.debug('finalizeError', finalizeError);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    form.setFieldsValue(
      populateFormOnLoad(travelProposal, travelMethodId, travelEligibilitySelected),
    );
  }, [travelProposal?.departureTime]);

  useEffect(() => {
    if (travelMethodId === TravelMethodId.GroundTransportation) {
      const currentValue = form.getFieldValue('groundTransportationCompanyId');
      if (!currentValue) {
        const defaultOption = LeashContent?.groundTransportationCompanies?.find(
          (company: TransporterCompany) => company.name === 'Other',
        );

        if (defaultOption) {
          form.setFieldsValue({ groundTransportationCompanyId: defaultOption.id });
        }
      }
    }
  }, [travelMethodId, form]);

  return (
    <ProposalStyled>
      <DetailsHeader readOnly={isCanceled(workflowStatus)} />
      <Divider plain />
      <Row className="proposal-form-header" justify="space-between" align="middle">
        <h2>Travel Plan</h2>
      </Row>
      <ProposalFormStyled
        size="large"
        requiredMark={true}
        onFinish={handleFormOnFinish}
        form={form}
        validateTrigger="onSubmit"
        readOnly={isCanceled(workflowStatus) || isFinalized(workflowStatus)}
        layout="inline"
      >
        <Form.Item className="select-method-form-item">
          <SelectTravelMethod
            travelMethods={buildEligibilityTravelMethods(
              travelPackage.travelTypeId,
              travelEligibility,
            )}
            defaultValue={travelMethodId}
            disabled={true}
          />
        </Form.Item>
        {travelMethodId === TravelMethodId.CharterFlight ? (
          <FieldDate
            name="arrivalTime"
            label="Flight date"
            required={false}
            disabled={isSaving}
            loading={isSaving}
          />
        ) : (
          <FieldDate
            name="departureTime"
            label="Estimated departure date"
            required={false}
            disabled={isSaving}
            loading={isSaving}
          />
        )}
        {travelMethodId === TravelMethodId.GroundTransportation && (
          <FieldGroundTransporter
            name="groundTransportationCompanyId"
            label="Transportation companies"
            required={false}
            loading={isSaving}
            disabled={isSaving}
            style={{ width: '420px' }}
          />
        )}
        <Button
          type="primary"
          loading={isSaving}
          disabled={isCanceled(workflowStatus) || isFinalized(workflowStatus) || isSaving}
          onClick={handleSaveFormData}
        >
          Save
        </Button>
        <ProposalButtons>
          <MapRouteLink origin={travelPlan.breeder} destination={travelPlan.customer} />
          <Notes
            travelPackageId={travelPackage.id}
            leadId={travelPlan.leadId}
            readOnly={isCanceled(workflowStatus)}
          />
          <Tooltip
            placement="topLeft"
            title="Please make sure you have added and saved sequences."
            trigger={isDisabledFinalizeButton ? 'hover' : ''}
          >
            <Button
              type="primary"
              htmlType="submit"
              loading={isSubmitting}
              disabled={isDisabledFinalizeButton}
              data-testid={testId.finalizedTravelPackageButton}
            >
              Finalize
            </Button>
          </Tooltip>
        </ProposalButtons>
      </ProposalFormStyled>
    </ProposalStyled>
  );
}
